import { useTranslation } from 'react-i18next';
import { IpponButton, IpponHSpace, IpponText } from '@ippon-ui/react';
import type { Optional } from '@/generic/functional';
import type { Page } from '@/generic/pagination';
import type { DraftRule, DraftRuleEdit } from '@/rules-management/domain/DraftRule.ts';
import type { DraftRulesSelection } from '@/rules-management/domain/DraftRulesSelection.ts';
import type { DraftRulesPublication } from '@/rules-management/domain/DraftRulesPublication.ts';
import { DraftRulesContent } from '@/rules-management/primary/DraftRulesContent.tsx';

const PublicationFailureComponent = ({ publication }: { publication: DraftRulesPublication }) => {
  const { t } = useTranslation('rules-management');

  const message = publication.hasPublished
    ? t('rules-management.rules.import.result.publication.partial-failure', {
        published: publication.publishedCount,
        failed: publication.failedCount,
      })
    : t('rules-management.rules.import.result.publication.total-failure');

  return (
    <div className="ippon-modal--actions">
      <IpponText
        variant="body"
        size="small"
        color="error-primary"
        dataSelector="rules-management.rules.import.result.publication-error"
      >
        {message}
      </IpponText>
    </div>
  );
};

const DiscardFailureComponent = () => {
  const { t } = useTranslation('rules-management');

  return (
    <div className="ippon-modal--actions">
      <IpponText
        variant="body"
        size="small"
        color="error-primary"
        dataSelector="rules-management.rules.import.result.discard-error"
      >
        {t('rules-management.rules.import.result.discard-error')}
      </IpponText>
    </div>
  );
};

const EditFailureComponent = () => {
  const { t } = useTranslation('rules-management');

  return (
    <div className="ippon-modal--actions">
      <IpponText
        variant="body"
        size="small"
        color="error-primary"
        dataSelector="rules-management.rules.import.result.edit-error"
      >
        {t('rules-management.rules.import.result.edit-error')}
      </IpponText>
    </div>
  );
};

export type DraftRulesActionsProps = {
  selection: DraftRulesSelection;
  publicationFailure: Optional<DraftRulesPublication>;
  discardFailure: Optional<Error>;
  editFailure: Optional<Error>;
  onSelectionChange: (selection: DraftRulesSelection) => void;
  onPageChange: (page: number) => void;
  onPublish: () => Promise<void>;
  onDiscard: (draft: DraftRule, page: number) => void;
  onSave: (draft: DraftRule, edit: DraftRuleEdit, page: number) => Promise<boolean>;
  onClose: () => void;
};

export const DraftRulesPublicationComponent = ({
  drafts,
  selection,
  publicationFailure,
  discardFailure,
  editFailure,
  onSelectionChange,
  onPageChange,
  onPublish,
  onDiscard,
  onSave,
  onClose,
}: DraftRulesActionsProps & {
  drafts: Page<DraftRule>;
}) => {
  const { t } = useTranslation('rules-management');

  return (
    <>
      {publicationFailure
        .map((publication) => (
          <PublicationFailureComponent
            key="rules-management.rules.import.result.publication-error"
            publication={publication}
          />
        ))
        .orUndefined()}
      {discardFailure
        .map(() => (
          <DiscardFailureComponent key="rules-management.rules.import.result.discard-error" />
        ))
        .orUndefined()}
      {editFailure
        .map(() => <EditFailureComponent key="rules-management.rules.import.result.edit-error" />)
        .orUndefined()}
      <DraftRulesContent
        drafts={drafts}
        selection={selection}
        onSelectionChange={onSelectionChange}
        onPageChange={onPageChange}
        onDiscard={onDiscard}
        onSave={onSave}
      />
      <div className="ippon-modal--footer">
        <IpponHSpace gap={8}>
          <IpponButton
            color="neutral"
            variant="outline"
            onClick={onClose}
            dataSelector="rules-management.rules.import.result.close"
          >
            {t('rules-management.rules.import.result.close')}
          </IpponButton>
          <IpponButton
            onClick={onPublish}
            disabled={selection.isEmpty}
            dataSelector="rules-management.rules.import.result.publish"
          >
            {t('rules-management.rules.import.result.publish', { count: selection.count })}
          </IpponButton>
        </IpponHSpace>
      </div>
    </>
  );
};
